import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  BookOpen, 
  Layers, 
  Clock, 
  Edit3, 
  Save, 
  Upload, 
  X, 
  Eye, 
  Trash2,
  Plus
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import spAssembly from '../../../assets/SPPathwayAssembly.png';
import spCamping from '../../../assets/SPPathwayCamping.png';
import spActivity from '../../../assets/SPPathwayActivity.jpg';
import spCoreValues from '../../../assets/SPPathwayCoreValues.png';
import spPE from '../../../assets/SPPathwayPE.png';
import spLab from '../../../assets/SPPathwayLab.png';
import './FormsUnified.css';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const DEFAULT_SPECIAL_PROGRAM_IMAGES = [
  spAssembly,
  spCamping,
  spActivity,
  spCoreValues,
  spPE,
  spLab
];

const getSpecialProgramFallback = (title, idx) => {
  const lower = (title || '').toLowerCase();
  if (lower.includes('bridg')) return spAssembly;
  if (lower.includes('club')) return spCamping;
  if (lower.includes('leader')) return spActivity;
  if (lower.includes('communit') || lower.includes('service')) return spCoreValues;
  if (lower.includes('eca') || lower.includes('extra-curricular')) return spPE;
  if (lower.includes('talent')) return spLab;
  return DEFAULT_SPECIAL_PROGRAM_IMAGES[idx % DEFAULT_SPECIAL_PROGRAM_IMAGES.length] || spAssembly;
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationSpecialProgramsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const defaults = EDUCATION_DEFAULTS.special_programs;

  const defaultStats = [
    { label: 'Program Pathways', value: '6' },
    { label: 'Student Activities', value: 'Weekly' },
    { label: 'Participation Options', value: 'All Levels' }
  ];

  const [formData, setFormData] = useState({
    kicker: 'HOLISTIC DEVELOPMENT & ENRICHMENT',
    title: 'Special Programs & Enrichment Pathways',
    subtitle: 'Nurturing talents, leadership, and real-world impact beyond the classroom',
    description: 'Our special programs empower students to discover their unique strengths through bridging support, leadership initiatives, competitive talent coaching, and community service.',
    note: 'Co-curricular pathways run in synergy with the Cambridge International Curriculum.',
    stats: defaultStats,
    programs: Array.isArray(defaults) ? defaults.map(p => ({ ...p })) : []
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Program Modal states (Add / Edit)
  const [editingProgramIndex, setEditingProgramIndex] = useState(null);
  const [isAddingProgram, setIsAddingProgram] = useState(false);
  const [programModalData, setProgramModalData] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);

  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationSpecialProgramsForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const pickBestSection = (sectionsList) => {
    if (!Array.isArray(sectionsList)) return null;
    const candidates = sectionsList.filter(s => 
      s.section_type === 'education-special-programs' || 
      s.section_type === 'education-special-programs-shell'
    );
    if (!candidates.length) return null;

    // 1. Priority: candidate that has actual program images populated
    const withImages = candidates.find(s => {
      let c = s.content_published || s.content || {};
      if (typeof c === 'string') {
        try { c = JSON.parse(c); } catch (e) { c = {}; }
      }
      const progs = c?.en?.programs || c?.programs;
      return Array.isArray(progs) && progs.some(p => p.image && String(p.image).trim() !== '');
    });
    if (withImages) return withImages;

    // 2. Candidate with non-empty programs array
    const withProgs = candidates.find(s => {
      let c = s.content_published || s.content || {};
      if (typeof c === 'string') {
        try { c = JSON.parse(c); } catch (e) { c = {}; }
      }
      const progs = c?.en?.programs || c?.programs;
      return Array.isArray(progs) && progs.length > 0;
    });
    if (withProgs) return withProgs;

    // 3. Candidate with section id 146
    const id146 = candidates.find(s => s.id === 146);
    if (id146) return id146;

    return candidates[0];
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let section = null;

      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers: getAuthHeaders() }
      );

      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          section = pickBestSection(result.data);
        }
      }

      if (!section) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers: getAuthHeaders() }
        );

        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            section = pickBestSection(result.data);
          }
        }
      }

      if (section) {
        setSectionId(section.id);

        let content = section.content_published || section.content || {};
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch (e) { content = {}; }
        }
        const en = content.en || {};

        const loadedPrograms = Array.isArray(en.programs) && en.programs.length > 0 
          ? en.programs.map(p => ({ ...p })) 
          : Array.isArray(defaults) 
          ? defaults.map(p => ({ ...p })) 
          : [];

        const loadedStats = Array.isArray(en.stats) && en.stats.length > 0 
          ? en.stats.map(s => ({ ...s })) 
          : defaultStats.map(s => ({ ...s }));

        // Ensure Metric 1 value automatically reflects directory count
        if (loadedStats[0]) {
          loadedStats[0].value = String(loadedPrograms.length);
        }

        const initialData = {
          kicker: en.kicker || 'HOLISTIC DEVELOPMENT & ENRICHMENT',
          title: en.title || 'Special Programs & Enrichment Pathways',
          subtitle: en.subtitle || 'Nurturing talents, leadership, and real-world impact beyond the classroom',
          description: en.description || 'Our special programs empower students to discover their unique strengths through bridging support, leadership initiatives, competitive talent coaching, and community service.',
          note: en.note || 'Co-curricular pathways run in synergy with the Cambridge International Curriculum.',
          stats: loadedStats,
          programs: loadedPrograms
        };

        const pendingFieldsMap = {};
        try {
          const pendingResponse = await fetch(
            `${apiBase}/cms/section/${section.id}/with-pending`,
            { credentials: 'include', headers: getAuthHeaders() }
          );

          if (pendingResponse.ok) {
            const pendingResult = await pendingResponse.json();
            if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
              const { pending_changes } = pendingResult.data;
              if (Array.isArray(pending_changes)) {
                pending_changes.forEach(change => {
                  if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                    let val = change.new_value;
                    if (typeof val === 'string') {
                      try { val = JSON.parse(val); } catch (e) {}
                    }

                    if (['kicker', 'title', 'subtitle', 'description', 'note'].includes(change.field_id)) {
                      pendingFieldsMap[change.field_id] = true;
                      if (typeof val === 'string') {
                        initialData[change.field_id] = val;
                      }
                    } else if (change.field_id === 'stats') {
                      pendingFieldsMap.stats = true;
                      if (Array.isArray(val)) {
                        initialData.stats = val;
                      }
                    } else if (change.field_id && change.field_id.includes('programs.')) {
                      const match = change.field_id.match(/programs\.(\d+)\.(add|update|delete)/);
                      if (match) {
                        const idx = parseInt(match[1], 10);
                        const op = match[2];
                        pendingFieldsMap[`program_${idx}`] = true;
                        if (op === 'delete') {
                          pendingFieldsMap[`program_${idx}_delete`] = true;
                        } else if (op === 'add') {
                          pendingFieldsMap[`program_${idx}_add`] = true;
                          if (val && typeof val === 'object' && !initialData.programs[idx]) {
                            initialData.programs.push(val);
                          }
                        } else if (op === 'update') {
                          if (val && typeof val === 'object' && initialData.programs[idx]) {
                            initialData.programs[idx] = val;
                          }
                        }
                      }
                    }
                  }
                });
              }
            }
          }
        } catch (err) {
          console.warn('[EducationSpecialProgramsForm] Pending check error:', err.message);
        }

        setFormData(initialData);
        setOriginalData(JSON.parse(JSON.stringify(initialData)));
        setPendingFields(pendingFieldsMap);
      } else {
        toast.error('Special Programs section not found');
      }
    } catch (err) {
      console.error('[EducationSpecialProgramsForm] Load error:', err);
      toast.error('Failed to load special programs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleStatChange = (idx, field, value) => {
    setFormData(prev => {
      const updated = [...prev.stats];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, stats: updated };
    });
  };

  // Submit Single Overview Field
  const handleSubmitSingleField = async (fieldKey, fieldLabel) => {
    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const value = (formData[fieldKey] || '').trim();
    if (!value && fieldKey !== 'note') {
      toast.warning(`Please enter ${fieldLabel}`);
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldKey,
            field_name: `Special Programs ${fieldLabel}`,
            old_value: originalData[fieldKey] || '',
            new_value: formData[fieldKey],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`${fieldLabel} proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
        setOriginalData(prev => ({ ...prev, [fieldKey]: formData[fieldKey] }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for ${fieldLabel} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(`Error submitting ${fieldLabel}: ` + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  // Submit Statistics Metrics (Metric 1 is automatically computed from programs directory)
  const handleSubmitStats = async () => {
    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    // Construct clean payload: Metric 1 is always Program Pathways with auto-count
    const statsPayload = [
      { label: 'Program Pathways', value: String(formData.programs?.length || 0) },
      formData.stats?.[1] || { label: 'Student Activities', value: 'Weekly' },
      formData.stats?.[2] || { label: 'Participation Options', value: 'All Levels' }
    ];

    setSubmitting(prev => ({ ...prev, stats: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: 'stats',
            field_name: 'Special Programs Statistics Metrics',
            old_value: originalData.stats || [],
            new_value: statsPayload,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success('Statistics metrics proposed for Principal approval');
        setPendingFields(prev => ({ ...prev, stats: true }));
        setFormData(prev => ({ ...prev, stats: statsPayload }));
        setOriginalData(prev => ({ ...prev, stats: statsPayload }));
      } else {
        if (response.status === 409) {
          toast.warning('A pending proposal for Statistics is already awaiting review');
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit stats error:', err);
      toast.error('Error submitting statistics: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, stats: false }));
    }
  };

  // Open modal to add a brand new program
  const handleOpenAddProgramModal = () => {
    setIsAddingProgram(true);
    setEditingProgramIndex(null);
    setProgramModalData({
      title: '',
      description: '',
      image: ''
    });
  };

  // Open modal to edit existing program
  const openEditProgramModal = (index) => {
    setIsAddingProgram(false);
    setEditingProgramIndex(index);
    setProgramModalData({ ...formData.programs[index] });
  };

  const closeProgramModal = () => {
    setIsAddingProgram(false);
    setEditingProgramIndex(null);
    setProgramModalData(null);
    setImageUploading(false);
  };

  // Replace / Upload Program Image (Only Replace, No Remove Image)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const resp = JSON.parse(xhr.responseText);
            if (resp.success || resp.ok) {
              const mediaId = resp.media_id || resp.id;
              const mediaPath = resp.file_path || resp.url;
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const fullUrl = mediaPath 
                ? (mediaPath.startsWith('http') ? mediaPath : `${origin}${mediaPath}`)
                : String(mediaId);

              if (fullUrl) {
                setProgramModalData(prev => ({ ...prev, image: fullUrl }));
                if (mediaId) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: fullUrl,
                    [String(mediaId)]: fullUrl
                  }));
                }
                toast.success('Program photo uploaded successfully');
              }
            } else {
              toast.error(resp.error || 'Failed to upload photo');
            }
          } catch (pe) {
            toast.error('Invalid server response during image upload');
          }
        } else {
          toast.error('Upload failed with status ' + xhr.status);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Network error during photo upload');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-special-programs-shell`);
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send(uploadData);
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Upload failed: ' + err.message);
      setImageUploading(false);
    }
  };

  // Delete Special Program Proposal
  const handleDeleteProgram = async (index) => {
    const targetProgram = formData.programs[index];
    if (!targetProgram) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetProgram.title}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `delete_program_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `programs.${index}.delete`,
            field_name: `Delete Special Program: ${targetProgram.title}`,
            old_value: targetProgram,
            new_value: null,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Deletion proposal for "${targetProgram.title}" submitted for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`program_${index}`]: true }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for Program ${index + 1} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Deletion proposal failed');
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to submit deletion proposal: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Save Program (Add or Update)
  const handleSaveProgramModal = async () => {
    if (!programModalData?.title?.trim()) {
      toast.warning('Please enter program title');
      return;
    }

    if (!programModalData?.description?.trim()) {
      toast.warning('Please enter program description');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const isAdd = isAddingProgram;
    const targetIndex = isAdd ? (formData.programs?.length || 0) : editingProgramIndex;
    const submitKey = isAdd ? 'add_program' : `program_${targetIndex}`;
    setSubmitting(prev => ({ ...prev, [submitKey]: true }));

    const fieldId = isAdd ? `programs.${targetIndex}.add` : `programs.${targetIndex}.update`;
    const fieldName = isAdd 
      ? `Add Special Program: ${programModalData.title}` 
      : `Update Special Program ${targetIndex + 1}: ${programModalData.title}`;
    const oldValue = isAdd ? null : (originalData.programs[targetIndex] || {});

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: programModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Program "${programModalData.title}" proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`program_${targetIndex}`]: true }));
        
        if (isAdd) {
          const updatedPrograms = [...formData.programs, { ...programModalData }];
          setFormData(prev => ({
            ...prev,
            programs: updatedPrograms,
            stats: prev.stats.map((s, idx) => idx === 0 ? { ...s, value: String(updatedPrograms.length) } : s)
          }));
        } else {
          const updatedPrograms = [...formData.programs];
          updatedPrograms[targetIndex] = { ...programModalData };
          setFormData(prev => ({ ...prev, programs: updatedPrograms }));
        }

        closeProgramModal();
      } else {
        if (response.status === 409) {
          toast.warning(`This program already has a pending proposal awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit program error:', err);
      toast.error('Failed to submit program: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [submitKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-form-container">
        <div className="unified-loading-container">
          <div className="unified-loading-spinner"></div>
          <p className="unified-loading-text">Loading Special Programs & Enrichment content...</p>
        </div>
      </div>
    );
  }

  const autoProgramCount = formData.programs?.length || 0;

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Sparkles size={22} />
          </div>
          <div>
            <h2 className="unified-title">Special Programs & Enrichment Pathways</h2>
            <p className="unified-subtitle">
              Configure transition initiatives, talent development coaching, leadership programs, and co-curricular pathways
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs Navigation */}
      <div className="unified-tab-bar" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BookOpen size={16} />
          Overview & Metrics
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'programs' ? 'active' : ''}`}
          onClick={() => setActiveTab('programs')}
        >
          <Layers size={16} />
          Programs Directory ({autoProgramCount})
        </button>
      </div>

      {/* TAB 1: OVERVIEW & METRICS */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
              Special Programs Header & Overview
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
              Configure headline text, overarching mission description, and statistical highlights
            </p>
          </div>

          {/* Section Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Tag / Kicker</label>
              {pendingFields.kicker && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields.kicker || submitting.kicker}
                placeholder="e.g. HOLISTIC DEVELOPMENT & ENRICHMENT"
                maxLength={80}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('kicker', 'Section Kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Section Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Title / Headline</label>
              {pendingFields.title && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields.title || submitting.title}
                placeholder="e.g. Special Programs & Enrichment Pathways"
                maxLength={120}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('title', 'Section Title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Section Subtitle */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Subtitle / Tagline</label>
              {pendingFields.subtitle && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.subtitle}
                onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                disabled={pendingFields.subtitle || submitting.subtitle}
                placeholder="e.g. Nurturing talents, leadership, and real-world impact beyond the classroom"
                maxLength={160}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('subtitle', 'Section Subtitle')}
                disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
              >
                <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Section Description */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Description</label>
              {pendingFields.description && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                className="unified-textarea"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={pendingFields.description || submitting.description}
                rows={4}
                placeholder="Detail how enrichment pathways empower students to discover their strengths..."
                maxLength={600}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('description', 'Section Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Footer Note / Synergy Tag */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Curriculum Synergy Note</label>
              {pendingFields.note && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.note}
                onChange={(e) => handleFieldChange('note', e.target.value)}
                disabled={pendingFields.note || submitting.note}
                placeholder="e.g. Co-curricular pathways run in synergy with the Cambridge International Curriculum."
                maxLength={180}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('note', 'Curriculum Synergy Note')}
                disabled={submitting.note || pendingFields.note || formData.note === originalData.note}
              >
                <Save size={14} /> {submitting.note ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Key Statistics Metrics (Overview Bar) */}
          <div className="unified-field-card" style={{ borderLeftColor: '#6366f1' }}>
            <div className="unified-field-header">
              <div>
                <label className="unified-label" style={{ color: '#4338ca' }}>
                  Key Statistics Metrics (Overview Bar)
                </label>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                  The primary metric (<strong>Program Pathways</strong>) is automatically calculated from your active directory ({autoProgramCount} pathways) and does not require manual input.
                </p>
              </div>
              {pendingFields.stats && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
              {/* Primary Metric Auto-Count Box (No manual input fields) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#f8fafc',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1.5px solid #e0e7ff'
                }}
              >
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Primary Metric (Automatic)
                  </span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginTop: '3px' }}>
                    Program Pathways: <span style={{ color: '#4338ca', fontWeight: 800 }}>{autoProgramCount} Pathways</span>
                  </div>
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#15803d',
                  background: '#dcfce7',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}>
                  Auto-calculated
                </span>
              </div>

              {/* Secondary Metrics Heading */}
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginTop: '4px' }}>
                Secondary Key Indicators (Customizable)
              </div>

              {/* Metric 2: Student Activities */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '12px',
                  background: '#f8fafc',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Metric Value
                  </span>
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.stats?.[1]?.value || ''}
                    onChange={(e) => handleStatChange(1, 'value', e.target.value)}
                    disabled={pendingFields.stats || submitting.stats}
                    placeholder="e.g. Weekly"
                    maxLength={40}
                  />
                </div>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Metric Label
                  </span>
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.stats?.[1]?.label || ''}
                    onChange={(e) => handleStatChange(1, 'label', e.target.value)}
                    disabled={pendingFields.stats || submitting.stats}
                    placeholder="e.g. Student Activities"
                    maxLength={80}
                  />
                </div>
              </div>

              {/* Metric 3: Participation Options */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '12px',
                  background: '#f8fafc',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Metric Value
                  </span>
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.stats?.[2]?.value || ''}
                    onChange={(e) => handleStatChange(2, 'value', e.target.value)}
                    disabled={pendingFields.stats || submitting.stats}
                    placeholder="e.g. All Levels"
                    maxLength={40}
                  />
                </div>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Metric Label
                  </span>
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.stats?.[2]?.label || ''}
                    onChange={(e) => handleStatChange(2, 'label', e.target.value)}
                    disabled={pendingFields.stats || submitting.stats}
                    placeholder="e.g. Participation Options"
                    maxLength={80}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={handleSubmitStats}
                  disabled={submitting.stats || pendingFields.stats}
                >
                  <Save size={14} />
                  {submitting.stats ? 'Saving...' : 'Save Statistics Metrics'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PROGRAMS DIRECTORY */}
      {activeTab === 'programs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
                Special Programs Directory
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                Structured pathways available for student participation, leadership, and enrichment
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleOpenAddProgramModal}
              style={{ padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Add Special Program
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Array.isArray(formData.programs) && formData.programs.map((program, idx) => {
              const isPending = pendingFields[`program_${idx}`];
              const imgSrc = getMediaUrl(program.image) || getSpecialProgramFallback(program.title, idx);
              const isDeleteSubmitting = submitting[`delete_program_${idx}`];

              return (
                <div
                  key={idx}
                  className="unified-field-card"
                  style={{
                    borderLeftColor: '#8b5cf6',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px'
                  }}
                >
                  <div className="unified-field-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        background: '#f5f3ff',
                        color: '#8b5cf6',
                        fontWeight: 800,
                        fontSize: '13px'
                      }}>
                        {idx + 1}
                      </span>
                      <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                        {program.title}
                      </h4>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isPending && (
                        <span 
                          className="unified-pending-pill"
                          style={pendingFields[`program_${idx}_delete`] ? { background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' } : {}}
                        >
                          <Clock size={12} /> {pendingFields[`program_${idx}_delete`] ? 'Pending Deletion' : pendingFields[`program_${idx}_add`] ? 'Pending Addition' : 'Pending Review'}
                        </span>
                      )}
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => openEditProgramModal(idx)}
                        disabled={isPending || isDeleteSubmitting}
                      >
                        <Edit3 size={14} />
                        Edit Program
                      </button>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => handleDeleteProgram(idx)}
                        disabled={isPending || isDeleteSubmitting}
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        title="Propose Deleting this Program"
                      >
                        <Trash2 size={14} />
                        {isDeleteSubmitting ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    {/* Image Thumbnail with Lightbox */}
                    <div
                      style={{
                        width: '120px',
                        height: '80px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        flexShrink: 0,
                        border: '1.5px solid #e2e8f0'
                      }}
                      onClick={() => setLightboxImage(imgSrc)}
                      title="Click to preview full image"
                    >
                      <img
                        src={imgSrc}
                        alt={program.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          const fallback = getSpecialProgramFallback(program.title, idx);
                          if (e.currentTarget.src !== fallback) {
                            e.currentTarget.src = fallback;
                          }
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        color: '#ffffff'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                      >
                        <Eye size={18} />
                      </div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                        {program.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PROGRAM ADD / EDIT MODAL */}
      {(editingProgramIndex !== null || isAddingProgram) && programModalData && (() => {
        const previewImg = (programModalData.image && getMediaUrl(programModalData.image)) || 
          (isAddingProgram ? spAssembly : getSpecialProgramFallback(programModalData.title, editingProgramIndex));
        const isCustomUpload = Boolean(programModalData.image && String(programModalData.image).trim());
        const modalSubmitting = isAddingProgram 
          ? submitting.add_program 
          : submitting[`program_${editingProgramIndex}`];

        return (
          <div className="unified-modal-overlay" onClick={closeProgramModal}>
            <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
              <div className="unified-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Sparkles size={20} color="#8b5cf6" />
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                    {isAddingProgram ? 'Add New Special Program' : `Edit Special Program: ${programModalData.title}`}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={closeProgramModal}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {/* Image Upload Area (Replace Only - No Remove Image) */}
                <div>
                  <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                    Program Photo / Feature Image
                  </label>

                  <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '140px',
                      height: '90px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1.5px solid #e2e8f0',
                      flexShrink: 0
                    }}>
                      <img
                        src={previewImg}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          const fallback = getSpecialProgramFallback(programModalData.title, editingProgramIndex || 0);
                          if (e.currentTarget.src !== fallback) {
                            e.currentTarget.src = fallback;
                          }
                        }}
                      />
                    </div>
                    <div>
                      <span style={{
                        fontSize: '11.5px',
                        fontWeight: 700,
                        color: isCustomUpload ? '#15803d' : '#475569',
                        background: isCustomUpload ? '#dcfce7' : '#f1f5f9',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        marginBottom: '6px'
                      }}>
                        {isCustomUpload ? 'Custom Uploaded Photo' : 'Default Pathway Illustration'}
                      </span>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                        Select a new image below to replace the existing photo.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label
                      htmlFor="program-image-file"
                      className="unified-secondary-btn"
                      style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
                    >
                      <Upload size={14} />
                      {imageUploading ? `Uploading (${imageProgress}%)...` : 'Replace Program Photo'}
                    </label>
                    <input
                      id="program-image-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                      style={{ display: 'none' }}
                    />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Max 5MB • JPG, PNG, WebP
                    </span>
                  </div>
                </div>

                <div>
                  <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                    Program Title *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={programModalData.title || ''}
                    onChange={(e) => setProgramModalData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Bridging Program or Leadership Summit"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                    Program Description *
                  </label>
                  <textarea
                    className="unified-textarea"
                    value={programModalData.description || ''}
                    onChange={(e) => setProgramModalData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Explain program goals, activities, and benefits to participants..."
                    maxLength={500}
                  />
                </div>
              </div>

              <div className="unified-modal-footer">
                <button type="button" className="unified-secondary-btn" onClick={closeProgramModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={handleSaveProgramModal}
                  disabled={modalSubmitting || imageUploading}
                >
                  <Save size={16} />
                  {modalSubmitting ? 'Submitting...' : isAddingProgram ? 'Propose New Program' : 'Submit Program Proposal'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* LIGHTBOX PREVIEW MODAL */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '85vw',
              maxHeight: '85vh',
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#000000',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.6)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{
                width: '100%',
                height: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
